import { useQuery } from '@tanstack/react-query';
import { useApiServices } from '../../providers';
import { IMAGE_TOKEN_TEMPLATE } from './useFileUrlWithClerkToken';

/**
 * One key for the whole app, on purpose — see the query below.
 *
 * Exported so the mobile app can keep this out of its persisted cache: the value is a bearer token,
 * and the persister writes to disk with a 30-day `maxAge`.
 */
export const IMAGE_URL_TOKEN_QUERY_KEY = ['imageUrlToken'] as const;

/**
 * The Clerk token every signed image URL is built from.
 *
 * Deliberately keyed on nothing but itself. A file list mounts one image component per row, and when
 * each row asked for its own token that was one query and one `getToken` per visible file for a value
 * that is identical across all of them. Sharing one entry means a folder of 200 images holds one token
 * in the cache instead of 200 copies of it, spread across 1800 URL strings.
 *
 * `enabled` is per-observer: a component with no file id won't trigger the fetch, but still reads the
 * token once some other component has asked for it.
 */
export const useImageUrlTokenQuery = ({ enabled }: { enabled: boolean }) => {
  const { getToken } = useApiServices();

  return useQuery({
    queryKey: IMAGE_URL_TOKEN_QUERY_KEY,
    queryFn: () => getToken({ template: IMAGE_TOKEN_TEMPLATE }),
    enabled,
    // Clerk hands back its own cached token until shortly before expiry, so a refetch here is usually
    // just a resolved promise. When the token hasn't rotated the string is identical, structural
    // sharing keeps the same reference, and nothing downstream re-renders.
    staleTime: 5 * 60 * 1000,
    // One failure now blanks every image on screen rather than one, so unlike the old per-file query
    // this is worth a second attempt — but not the shared default of 3, which would sit on a dead
    // token while the placeholders show.
    retry: 1,
  });
};
